import { io, type Socket } from 'socket.io-client';

export type Topic = 'diagram' | 'assistant';

/** Ответ на `subscribe` (docs/architecture.md §7.6): данные темы или ошибка с кодом. */
export type SubscribeAck = { data: unknown } | { error: { code: string; message?: string } };

interface Subscription {
  topic: Topic;
  workspaceId: string;
  id: string;
  /** Подписка (пере)установлена: после разрыва подписчик сверяет ревизию и догружает пропущенное. */
  onAck: (ack: SubscribeAck) => void;
}

type SocketFactory = () => Socket;

/** Через сколько повторить подключение, если сервер его отклонил или закрыл сам. */
const RETRY_MS = 5_000;
const ACK_TIMEOUT_MS = 10_000;

/**
 * Одно подключение Socket.IO на вкладку (namespace `/rt`) и общие подписки на темы.
 * Несколько подписчиков одной темы делят одну подписку на сервере; после переподключения
 * подписки восстанавливаются сами, и каждый подписчик получает новый ack.
 *
 * Сервер отключает сокеты при отзыве доступа (выход, удаление из пространства, архив):
 * клиент подключается снова и подписывается там, где доступ остался. Отказ на handshake
 * (сессии больше нет) сообщается `onSessionLost`, чтобы приложение перепроверило вход.
 */
export class RealtimeClient {
  private socket: Socket | null = null;
  private readonly subscriptions = new Map<string, Set<Subscription>>();
  private readonly statusListeners = new Set<(connected: boolean) => void>();
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  constructor(
    private readonly options: {
      onSessionLost?: () => void;
      createSocket?: SocketFactory;
    } = {},
  ) {}

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  /** Подписаться на тему; возвращает отписку. */
  subscribe(subscription: Subscription): () => void {
    const key = subscriptionKey(subscription.topic, subscription.id);
    let group = this.subscriptions.get(key);
    const isFirst = group === undefined;
    if (group === undefined) {
      group = new Set();
      this.subscriptions.set(key, group);
    }
    group.add(subscription);
    const socket = this.ensureSocket();
    if (socket.connected) {
      // Первый подписчик подписывает сервер; следующие получают свежий ack той же командой.
      void this.sendSubscribe(
        subscription.topic,
        subscription.workspaceId,
        subscription.id,
        isFirst ? group : new Set([subscription]),
      );
    }
    return () => {
      const current = this.subscriptions.get(key);
      if (current === undefined) return;
      current.delete(subscription);
      if (current.size > 0) return;
      this.subscriptions.delete(key);
      if (this.socket?.connected) {
        this.socket.emit('unsubscribe', { topic: subscription.topic, id: subscription.id });
      }
    };
  }

  /** Обработчик события сервера (`diagram.changed`, `assistant.event`…); возвращает отписку. */
  on<T>(event: string, handler: (payload: T) => void): () => void {
    const socket = this.ensureSocket();
    socket.on(event, handler);
    return () => {
      socket.off(event, handler);
    };
  }

  onStatus(listener: (connected: boolean) => void): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  close(): void {
    this.closed = true;
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.socket?.disconnect();
    this.socket = null;
    this.subscriptions.clear();
  }

  private ensureSocket(): Socket {
    if (this.socket !== null) return this.socket;
    const socket =
      this.options.createSocket?.() ??
      io('/rt', { transports: ['websocket'], withCredentials: true });
    this.socket = socket;
    socket.on('connect', () => {
      this.emitStatus(true);
      for (const group of this.subscriptions.values()) {
        const first = group.values().next().value;
        if (first !== undefined)
          void this.sendSubscribe(first.topic, first.workspaceId, first.id, group);
      }
    });
    socket.on('disconnect', (reason: string) => {
      this.emitStatus(false);
      // Сервер закрыл подключение сам (отзыв доступа) — Socket.IO в этом случае не переподключается.
      if (reason === 'io server disconnect') this.scheduleRetry();
    });
    socket.on('connect_error', () => {
      // Отказ на handshake: сессии могло не стать. Повтор — только после перепроверки входа.
      if (!socket.active) {
        this.options.onSessionLost?.();
        this.scheduleRetry();
      }
    });
    return socket;
  }

  private scheduleRetry(): void {
    if (this.closed || this.retryTimer !== null) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.closed && this.socket !== null && !this.socket.connected) this.socket.connect();
    }, RETRY_MS);
  }

  private async sendSubscribe(
    topic: Topic,
    workspaceId: string,
    id: string,
    receivers: ReadonlySet<Subscription>,
  ): Promise<void> {
    const socket = this.socket;
    if (socket === null) return;
    const ack = (await socket
      .timeout(ACK_TIMEOUT_MS)
      .emitWithAck('subscribe', { topic, workspaceId, id })
      .catch(() => ({ error: { code: 'REALTIME_TIMEOUT' } }))) as SubscribeAck;
    const current = this.subscriptions.get(subscriptionKey(topic, id));
    // Отписался, пока ждал ответа, — ack ему уже не нужен.
    for (const subscription of receivers) {
      if (current?.has(subscription) === true) subscription.onAck(ack);
    }
  }

  private emitStatus(connected: boolean): void {
    for (const listener of this.statusListeners) listener(connected);
  }
}

function subscriptionKey(topic: Topic, id: string): string {
  return `${topic}:${id}`;
}
